import * as React from "react";
import "./index.css";
import ModalBase from "../../ModalBase";
import { PaymentMethodTile } from "../../../Tiles";
import Button from "../../../Button";
import { AddIcon } from "../../../icons";
import { OutlinedInput } from "../../../Input";
import { useSelector } from "react-redux";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { selectAuth } from "../../../../features/auth/authSlice";
import {
  useCreateSetupIntentMutation,
  useDetachPaymentMethodMutation,
  useGetCustomerQuery,
  useListPaymentMethodsQuery,
  useSetDefaultPaymentMethodMutation,
} from "../../../../features/stripe/stripeSlice";
import { toast } from "react-toastify";

const PaymentInfoModal = ({ isOpen, onClose }) => {
  const baseRef = React.useRef();
  const auth = useSelector(selectAuth);
  const stripe = useStripe();
  const elements = useElements();
  const toastId = React.useRef(null);

  const [addNewActive, setAddNewActive] = React.useState(false);
  

  const [newCard, setNewCard] = React.useState({
    holderName: "",
    cardNumber: "",
    expiry: "",
    cvc: "",
  });

  const {
    data: paymentMethods,
    isFetching,
    isLoading,
    refetch: refetchPaymentMethods,
  } = useListPaymentMethodsQuery(auth.user?.customer_id, {
    skip: !auth.user?.customer_id,
  });

  const {
    data: customerData,
    refetch: refetchCustomerData,
    isLoading: loadingUser,
    isFetching: fetchingUser,
  } = useGetCustomerQuery(auth.user?.customer_id, {
    skip: !auth.user?.customer_id,
  });

  const [setDefaultPaymentMethod] = useSetDefaultPaymentMethodMutation();

  const isMobile = React.useMemo(
    () => window.innerWidth < 768,
    [window.innerWidth]
  );

  const [createSetupIntent] = useCreateSetupIntentMutation();
  const [detachPaymentMethod] = useDetachPaymentMethodMutation();

  const handleAddNewCardInputChange = (e) =>
    setNewCard({ ...newCard, [e.target.name]: e.target.value });

  const onAddCardSubmit = async (e) => {
    e.preventDefault();

    console.log(elements.getElement(CardNumberElement))
    const {
      data: { clientSecret },
    } = await createSetupIntent({
      customerId: auth.user?.customer_id,
    });
    console.log(clientSecret);

    const res = await stripe.confirmCardSetup(clientSecret, {
      payment_method: {
        card: elements.getElement(CardNumberElement),
        billing_details: {
          name: newCard.holderName,
        },
      },
    });
    console.log(res);
    if(res.error) {
      toast.error(res.error.message);
      return;
    }
    setAddNewActive(false);
    setNewCard({
      holderName: "",
      cardNumber: "",
      expiry: "",
      cvc: "",
    });
    refetchPaymentMethods();
  };

  const ref = React.useRef();

  React.useEffect(() => {
    if (addNewActive) {
      ref.current.scrollTo({
        top: ref.current.scrollHeight,
        behavior: "smooth",
      });
      baseRef.current.style.transition = "height 0.5s";
      baseRef.current.style.height = "600px";
    }
  }, [addNewActive]);

  React.useEffect(() => {
    refetchPaymentMethods();
    refetchCustomerData();
  }, []);

  const handleSetDefault = async (pm) => {
    toastId.current = toast("Setting Default Payment Method...", {
      isLoading: true,
      autoClose: false,
      closeButton: false,
    });
    try {
      await setDefaultPaymentMethod({
        paymentMethodId: pm.id,
        customerId: auth.user?.customer_id,
      });
      await refetchPaymentMethods();
      await refetchCustomerData();
    } catch (error) {
      toast.update(toastId.current, {
        type: "error",
        render: "Error setting Default Payment Method",
        isLoading: false,
        autoClose: 3000,
        closeButton: true,
      });
    }
    toast.update(toastId.current, {
      type: "success",
      render: "Default Payment Method set",
      isLoading: false,
      autoClose: 3000,
      closeButton: true,
    });
  };

  const handleDelete = async (pm) => {
    toastId.current = toast("Deleting Payment Method...", {
      isLoading: true,
      autoClose: false,
      closeButton: false,
    });
    try {
      await detachPaymentMethod({ paymentMethodId: pm.id });
      await refetchPaymentMethods();
    } catch (error) {
      toast.update(toastId.current, {
        type: "error",
        render: "Error deleting Payment Method",
        isLoading: false,
        autoClose: 3000,
        closeButton: true,
      });
    }
    toast.update(toastId.current, {
      type: "success",
      render: "Payment Method deleted",
      isLoading: false,
      autoClose: 3000,
      closeButton: true,
    });
    
  };

  return (
    <ModalBase
      isOpen={isOpen}
      onClose={() => {
        setAddNewActive(false);
        onClose();
      }}
      title={"Payment methods"}
      width={"860px"}
    >
      <div className="payment-info-modal-base container" ref={baseRef}>
        <span className="modal-title">My Payment methods</span>
        <div className="payment-info-modal-body" ref={ref}>
          {isLoading && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100px",
              }}
            >
              <span className="loader" />
            </div>
          )}
          {!isLoading &&
            paymentMethods.map((pm, index) => (
              <PaymentMethodTile
                pm={pm}
                isDefault={
                  loadingUser || fetchingUser
                    ? true
                    : customerData?.invoice_settings.default_payment_method ===
                      pm.id
                }
                onSetDefault={() => handleSetDefault(pm)}
                onDelete={() => handleDelete(pm)}
              />
            ))}
          {!addNewActive ? (
            <span
              onClick={() => setAddNewActive(true)}
              style={{
                color: "var(--red)",
                textDecoration: "underline",
                cursor: "pointer",
                paddingLeft: "20px",
              }}
            >
              Add new Payment Method
            </span>
          ) : (
            <div
              style={{
                padding: "0 20px",
              }}
            >
              <div className="payment-info-add-payment-form-header">
                <span className="text-medium">Add New Payment Method</span>
                <span>Required field*</span>
              </div>
              <form
                className="checkout-add-payment-form"
                onSubmit={onAddCardSubmit}
              >
                <div>
                  <div className="checkout-input-title">Holder Name*</div>
                  <input
                    className="checkout-input"
                    placeholder="Name*"
                    onChange={handleAddNewCardInputChange}
                  />
                </div>
                <div>
                  <div className="checkout-input-title">Card Number*</div>
                  <CardNumberElement
                    options={{
                      iconStyle: "solid",
                      placeholder: "Credit Card number*",
                      showIcon: true,
                      classes: {
                        base: "checkout-input",
                      },
                      style: {
                        base: {
                          fontSize: isMobile ? "9px" : "16px",
                          backgroundColor: "white",
                        },
                      },
                    }}
                  />
                </div>
                <div className="checkout-input-row">
                  <div
                    style={{
                      flex: 1,
                    }}
                  >
                    <div className="checkout-input-title">Expiration Date*</div>
                    <CardExpiryElement
                      className="checkout-input"
                      options={{
                        placeholder: "--/--",
                        style: {
                          base: {
                            fontSize: isMobile ? "9px" : "16px",
                          },
                        },
                      }}
                    />
                  </div>
                  <div
                    style={{
                      flex: 1,
                    }}
                  >
                    <div className="checkout-input-title">CVC*</div>
                    <CardCvcElement
                      className="checkout-input"
                      options={{
                        iconStyle: "solid",
                        placeholder: "---",
                        style: {
                          base: {
                            fontSize: isMobile ? "9px" : "16px",
                          },
                        },
                      }}
                    />
                  </div>
                </div>
                <button className={"add-card-submit"} type={"submit"}>
                  Add Card
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </ModalBase>
  );
};

export default PaymentInfoModal;
